import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  DateUtilsService,
  DEFAULT_QUARTERS,
  type QuarterOption,
} from '../../../services/date-utils.service';

@Component({
  selector: 'app-periodo-trimestral',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './periodo-trimestral.component.html',
  styleUrls: ['./periodo-trimestral.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PeriodoTrimestralComponent implements OnInit {
  @Input() years: number[] = [];
  @Input() quarters: QuarterOption[] = [];
  /** ISO `yyyy-MM-dd` o vacío */
  @Input() maxDate: string | null = null;
  /** ISO `yyyy-MM-dd` o vacío; si es `null`/vacío se usa la mínima del sistema */
  @Input() minDate: string | null = null;

  @Input() selectedYear: number | null = null;
  @Output() selectedYearChange = new EventEmitter<number | null>();

  @Input() selectedQuarter: QuarterOption | null = null;
  @Output() selectedQuarterChange = new EventEmitter<QuarterOption | null>();

  constructor(private readonly dateUtilsService: DateUtilsService) {}

  ngOnInit(): void {
    const currentYear = this.dateUtilsService.getCurrentYear();
    const currentQuarter = this.dateUtilsService.getCurrentQuarterNumber();
    const minIso = this.dateUtilsService.getMinDate(this.minDate);
    const minYear = this.dateUtilsService.parseIsoToLocalDate(minIso).getFullYear();

    let maxYear: number;
    if (this.maxDate?.trim()) {
      const maxD = new Date(this.maxDate.trim());
      if (!Number.isNaN(maxD.getTime())) {
        maxYear = maxD.getFullYear();
      } else {
        maxYear = currentYear;
      }
    } else {
      maxYear = currentYear;
    }

    this.years = this.dateUtilsService.buildYearsRange(minYear, maxYear);

    if (!this.quarters.length) {
      this.quarters = [...DEFAULT_QUARTERS];
    }

    const parentHadYear = this.selectedYear !== null;
    const parentHadQuarter = this.selectedQuarter !== null;

    if (this.selectedYear === null) {
      this.selectedYear = Math.min(
        Math.max(currentYear, minYear),
        maxYear,
      );
    }

    this.quarters = this.dateUtilsService.buildQuartersRange(
      this.selectedYear!,
      this.minDate,
    );

    if (this.selectedQuarter === null) {
      this.selectedQuarter =
        this.quarters.find((q) => q.number === currentQuarter) ??
        this.quarters[this.quarters.length - 1] ??
        null;
    }

    if (!parentHadYear || !parentHadQuarter) {
      queueMicrotask(() => {
        if (!parentHadYear) {
          this.selectedYearChange.emit(this.selectedYear);
        }
        if (!parentHadQuarter) {
          this.selectedQuarterChange.emit(this.selectedQuarter);
        }
      });
    }
  }

  onYearModelChange(value: number | null): void {
    this.selectedYear = value;
    this.selectedYearChange.emit(this.selectedYear);
    this.quarters = this.dateUtilsService.buildQuartersRange(
      this.selectedYear!,
      this.minDate,
    );
    this.selectedQuarter = this.quarters[0] ?? null;
    this.selectedQuarterChange.emit(this.selectedQuarter);
  }

  onQuarterModelChange(value: QuarterOption | null): void {
    this.selectedQuarter = value;
    this.selectedQuarterChange.emit(this.selectedQuarter);
  }
}
